import { keyframes } from '@emotion/react';

export const EASE_OUT = 'cubic-bezier(0.23, 1, 0.32, 1)';
export const EASE_IN = 'cubic-bezier(0.55, 0, 1, 0.45)';
export const EASE_IN_OUT_STRONG = 'cubic-bezier(0.77, 0, 0.175, 1)';
export const EASE_DRAWER = 'cubic-bezier(0.32, 0.72, 0, 1)';

export const PAY_BUTTON_SHIMMER_CLASS = 'bundle-pay-button-shimmer';
export const PAY_BUTTON_SHIMMER_CSS = `
	@keyframes bundle-pay-button-shimmer-move {
		0% { transform: translateX(-150%) skewX(-18deg); }
		100% { transform: translateX(100%) skewX(-18deg); }
	}

	@media (prefers-reduced-motion: reduce) {
		.${PAY_BUTTON_SHIMMER_CLASS} {
			animation: none !important;
			opacity: 0 !important;
		}
	}
`;

export const PAY_BUTTON_SHIMMER_CONTAINER_CSS = {
	position: 'relative',
	overflow: 'hidden',
	isolation: 'isolate',
} as const;

export const PAY_BUTTON_SHIMMER_LAYER_PROPS = {
	position: 'absolute',
	top: 0,
	bottom: 0,
	left: 0,
	w: '150%',
	pointerEvents: 'none',
	zIndex: 1,
	bg: 'linear-gradient(90deg, rgba(139, 152, 161, 0) 0%, rgba(139, 152, 161, 0.35) 50%, rgba(139, 152, 161, 0) 100%)',
	transform: 'translateX(-150%) skewX(-18deg)',
	willChange: 'transform',
	animation: 'bundle-pay-button-shimmer-move 3s infinite ease',
} as const;

export const THINKING_LABEL_SHIMMER_CLASS = 'bundle-thinking-label-shimmer';
export const THINKING_LABEL_SHIMMER_DURATION_MS = 2200;
export const THINKING_LABEL_SHIMMER_CSS = `
	@keyframes bundle-thinking-label-shimmer {
		0% { background-position: 120% 50%; }
		70% { background-position: -45% 50%; }
		100% { background-position: -45% 50%; }
	}

	.${THINKING_LABEL_SHIMMER_CLASS} {
		background-image: linear-gradient(
			110deg,
			currentColor 0%,
			currentColor 32%,
			rgba(255, 255, 255, 0.98) 48%,
			currentColor 64%,
			currentColor 100%
		);
		background-size: 240% 100%;
		background-clip: text;
		-webkit-background-clip: text;
		-webkit-text-fill-color: transparent;
		animation: bundle-thinking-label-shimmer ${THINKING_LABEL_SHIMMER_DURATION_MS}ms ease infinite;
		animation-delay: -${THINKING_LABEL_SHIMMER_DURATION_MS / 2}ms;
	}

	@media (prefers-reduced-motion: reduce) {
		.${THINKING_LABEL_SHIMMER_CLASS} {
			animation: none !important;
			background-image: none !important;
			-webkit-text-fill-color: currentColor;
		}
	}
`;

export const PREPARING_STAGE_TEXT_SHIMMER_CLASS = 'bundle-preparing-stage-text-shimmer';
export const PREPARING_STAGE_TEXT_SHIMMER_DURATION_MS = 2000;
export const PREPARING_STAGE_TEXT_SHIMMER_CSS = `
	@keyframes bundle-preparing-stage-text-shimmer {
		0% { background-position: 120% 50%; }
		70% { background-position: -45% 50%; }
		100% { background-position: -45% 50%; }
	}

	.${PREPARING_STAGE_TEXT_SHIMMER_CLASS} {
		background-image: linear-gradient(
			110deg,
			currentColor 0%,
			currentColor 32%,
			rgba(255, 255, 255, 0.98) 48%,
			currentColor 64%,
			currentColor 100%
		);
		background-size: 240% 100%;
		background-clip: text;
		-webkit-background-clip: text;
		-webkit-text-fill-color: transparent;
		animation: bundle-preparing-stage-text-shimmer ${PREPARING_STAGE_TEXT_SHIMMER_DURATION_MS}ms ease infinite;
		animation-delay: -${PREPARING_STAGE_TEXT_SHIMMER_DURATION_MS / 2}ms;
	}

	@media (prefers-reduced-motion: reduce) {
		.${PREPARING_STAGE_TEXT_SHIMMER_CLASS} {
			animation: none !important;
			background-image: none !important;
			-webkit-text-fill-color: currentColor;
		}
	}
`;

export const SHIMMER_SWEEP_CLASS = 'bundle-shimmer-sweep';
export const DELAYED_READINESS_SHIMMER_DURATION_MS = 2800;
export const LOADING_ELLIPSIS_DOT_CLASS = 'bundle-loading-ellipsis-dot';

/**
 * Sidebar hover "window blinds" shutter: pill clips in from horizontal seam.
 * Open is faster than close so the reveal feels eager but the dismissal lingers.
 */
export const SIDEBAR_HOVER_OPEN_MS = 50;
export const SIDEBAR_HOVER_CLOSE_MS = 150;

/**
 * Apply to the parent of an element with `SIDEBAR_HOVER_PILL_CLASS`. The pill
 * is an absolutely-positioned background layer. Light mode uses a clip-path
 * "window blinds" shutter; dark mode switches to a "house lights" opacity fade
 * with a warm box-shadow glow (warmth=100, brightness=40, glow=40 preset from
 * the animation example). Keyboard focus reveals instantly (no animation,
 * high-frequency interaction rule). Hover is gated on `hover: hover and
 * pointer: fine` so touch taps don't leave the pill stuck open.
 */
export const SIDEBAR_HOVER_PILL_CLASS = 'sidebar-hover-pill';
const DARK_GLOW = '0 0 30px 16px rgba(255, 210, 135, 0.072)';
const DARK_GLOW_REST = '0 0 0 0 rgba(255, 210, 135, 0)';
// Dual selector: in the simple render path the focusable <a> wraps the styled
// Flex (ancestor focus → `a:focus-visible > &`); in the complex render path the
// <a> is inside the Flex (descendant focus → `&:has(:focus-visible)`).
// `&:focus-visible` covers items with no path, where the pill itself is the button.
const PILL_FOCUS_SELECTOR = `a:focus-visible > & .${SIDEBAR_HOVER_PILL_CLASS}, &:has(:focus-visible) .${SIDEBAR_HOVER_PILL_CLASS}, &:focus-visible .${SIDEBAR_HOVER_PILL_CLASS}`;
const DARK_PILL_FOCUS_SELECTOR = `.dark a:focus-visible > & .${SIDEBAR_HOVER_PILL_CLASS}, .dark &:has(:focus-visible) .${SIDEBAR_HOVER_PILL_CLASS}, .dark &:focus-visible .${SIDEBAR_HOVER_PILL_CLASS}`;
// Circular keyboard-focus ring on the pill. box-shadow — NOT outline — so it follows the pill's
// border-radius in every browser: Safari/WebKit render `outline` as a rectangle regardless of
// radius, which left the ring looking square. The focusable <a>'s own square outline is suppressed
// in SidebarItemLink. Same dual selector as the hover pill (the <a> is the Flex's parent in the
// simple render path, its descendant in the endElement path). A 2px outset ring stays within each
// item's 4px margin, so the sidebar's overflow:hidden doesn't clip it.
// NB: use --chakra-colors-brand-focus-ring, NOT --chakra-colors-focus-ring. The `focus.ring`
// semantic token does not emit a `--chakra-colors-focus-ring` var (it resolves to empty), which
// makes the whole box-shadow invalid and renders nothing. brand.focusRing (brand.500 / yellow) is
// the same intended colour and its var actually exists.
const FOCUS_RING_SHADOW = '0 0 0 2px var(--chakra-colors-brand-focus-ring)';
const SIDEBAR_FOCUS_RING_CSS = {
	'a:focus-visible > &, &:has(:focus-visible), &:focus-visible': {
		outline: 'none',
		boxShadow: FOCUS_RING_SHADOW,
	},
} as const;
export const SIDEBAR_HOVER_PILL_CSS = {
	...SIDEBAR_FOCUS_RING_CSS,
	// Light mode — window blinds (clip-path shutter)
	[`& .${SIDEBAR_HOVER_PILL_CLASS}`]: {
		clipPath: 'inset(50% 0 50% 0 round 9999px)',
		transition: `clip-path ${SIDEBAR_HOVER_CLOSE_MS}ms ${EASE_IN}`,
	},
	[PILL_FOCUS_SELECTOR]: {
		clipPath: 'inset(0 0 0 0 round 9999px)',
		transition: 'none',
	},
	// Dark mode — house lights (opacity fade + warm glow). clip-path stays
	// fully open; opacity and box-shadow do the animating instead.
	[`.dark & .${SIDEBAR_HOVER_PILL_CLASS}`]: {
		clipPath: 'inset(0 0 0 0 round 9999px)',
		opacity: 0,
		boxShadow: DARK_GLOW_REST,
		transition: `opacity ${SIDEBAR_HOVER_CLOSE_MS}ms ${EASE_IN}, box-shadow ${SIDEBAR_HOVER_CLOSE_MS}ms ${EASE_IN}`,
	},
	[DARK_PILL_FOCUS_SELECTOR]: {
		opacity: 1,
		boxShadow: DARK_GLOW,
		transition: 'none',
	},
	'@media (hover: hover) and (pointer: fine)': {
		[`&:hover .${SIDEBAR_HOVER_PILL_CLASS}`]: {
			clipPath: 'inset(0 0 0 0 round 9999px)',
			transition: `clip-path ${SIDEBAR_HOVER_OPEN_MS}ms ${EASE_OUT}`,
		},
		[`.dark &:hover .${SIDEBAR_HOVER_PILL_CLASS}`]: {
			opacity: 1,
			boxShadow: DARK_GLOW,
			transition: `opacity ${SIDEBAR_HOVER_OPEN_MS}ms ${EASE_OUT}, box-shadow ${SIDEBAR_HOVER_OPEN_MS}ms ${EASE_OUT}`,
		},
	},
	'@media (prefers-reduced-motion: reduce)': {
		[`& .${SIDEBAR_HOVER_PILL_CLASS}`]: {
			clipPath: 'inset(0 0 0 0 round 9999px)',
			opacity: 0,
			boxShadow: DARK_GLOW_REST,
			transition: 'opacity 150ms ease',
		},
		[PILL_FOCUS_SELECTOR]: {
			opacity: 1,
			transition: 'none',
		},
		'@media (hover: hover) and (pointer: fine)': {
			[`&:hover .${SIDEBAR_HOVER_PILL_CLASS}`]: {
				opacity: 1,
				transition: 'opacity 50ms ease',
			},
		},
	},
} as const;

/**
 * Apply to the active sidebar item's parent. In dark mode, hover/focus adds
 * the same warm box-shadow glow used by the hover pill — the active state
 * stays anchored but still gets the "lamp blooming on" affordance. No effect
 * in light mode (active is just a static filled pill).
 */
export const SIDEBAR_ACTIVE_GLOW_CSS = {
	...SIDEBAR_FOCUS_RING_CSS,
	transition: 'box-shadow 150ms ease, background-color 150ms ease',
	[`.dark a:focus-visible > &, .dark &:has(:focus-visible), .dark &:focus-visible`]: {
		// Same element as the ring, so stack them — otherwise this would replace the ring in dark mode.
		boxShadow: `${FOCUS_RING_SHADOW}, ${DARK_GLOW}`,
	},
	'@media (hover: hover) and (pointer: fine)': {
		'.dark &:hover': {
			boxShadow: DARK_GLOW,
		},
	},
} as const;

export const TRANSITION_BUTTON =
	'background-color 0.15s ease, color 0.15s ease, box-shadow 0.15s ease, transform 0.16s ease-out';

/**
 * Interaction-driven hover lift for buttons: a small rise on hover that settles back on press.
 * Gated behind real-hover pointers so a touch tap doesn't leave the button stuck raised, guarded
 * with :not(:disabled) so disabled/loading buttons stay put, and dropped under reduced-motion.
 * Apply via the `css` prop so it composes with a button's `_hover`.
 */
export const BUTTON_HOVER_LIFT_CSS = {
	transition: TRANSITION_BUTTON,
	'@media (hover: hover) and (pointer: fine)': {
		'&:not(:disabled):hover': { transform: 'translateY(-2px)' },
		'&:not(:disabled):active': { transform: 'translateY(0)' },
	},
	'@media (prefers-reduced-motion: reduce)': {
		'&:not(:disabled):hover, &:not(:disabled):active': { transform: 'none' },
	},
} as const;
export const TRANSITION_BG = 'background-color 0.1s ease';
export const TRANSITION_BORDER = 'border-color 0.2s ease';
export const TRANSITION_CARD_HOVER = 'box-shadow 0.15s ease';
export const TRANSITION_CARD_SURFACE = `${TRANSITION_BORDER}, ${TRANSITION_CARD_HOVER}`;
export const TRANSITION_CARD_INTERACTION = `${TRANSITION_CARD_SURFACE}, transform 0.2s ease`;
export const TRANSITION_CHEVRON = 'transform 0.2s ease';
export const TRANSITION_COLLAPSE = 'grid-template-rows 0.3s ease-out';
export const DURATION_ACCORDION_OPEN_MS = 180;
export const DURATION_ACCORDION_CLOSE_MS = 140;
export const DURATION_STATUS_PULSE_MS = 2400;
export const DURATION_SHEET_RESIZE_MS = 220;

/**
 * A completion first becomes a settled row so the result is legible, then leaves Upcoming. The
 * hold is feedback time, not animation time. The coordinated row-and-layout exit is slightly longer
 * than a micro-interaction because the neighbouring timeline can travel a full card height.
 */
export const PLAN_SETTLEMENT_HOLD_MS = 1200;
export const DURATION_PLAN_SETTLEMENT_MORPH_MS = 320;
export const DURATION_PLAN_SETTLEMENT_EXIT_MS = 360;
/** A card joining an existing day moves the rows below it far enough to need a calmer entrance. */
/** A stored custom entry fades into its already-reserved schedule slot. */
export const DURATION_PLAN_ENTRY_MS = 240;
export const DURATION_PLAN_SETTLEMENT_LAYOUT_EXIT_MS = 320;

/**
 * Inline form reveal (grid-template-rows 0fr→1fr expand). Opening is the user
 * requesting the form — entering, so ease-out. Closing is the system dismissing
 * it, kept ~20% faster (exit faster than enter). Uses the strong EASE_OUT curve
 * rather than the built-in keyword.
 */
export const DURATION_FORM_REVEAL_OPEN_MS = 300;
export const DURATION_FORM_REVEAL_CLOSE_MS = 160;
export const TRANSITION_FORM_REVEAL_OPEN = `grid-template-rows ${DURATION_FORM_REVEAL_OPEN_MS}ms ${EASE_OUT}`;
export const TRANSITION_FORM_REVEAL_CLOSE = `grid-template-rows ${DURATION_FORM_REVEAL_CLOSE_MS}ms ${EASE_OUT}`;
export const TRANSITION_DROPDOWN_REVEAL = 'max-height 0.25s ease, opacity 0.2s ease';

/**
 *TO DO
 * Controls that mount into an already-open container (e.g. the composer's add action row) slide
 * in from the edge they sit on + fade. Entrance only (@starting-style) — ease-out; transform +
 * opacity, so it's GPU-cheap and can't disturb layout. Reduced motion keeps just the fade.
 */
export const getSlideInCss = (from: 'left' | 'right', durationMs: number, distancePx: number) => ({
	transition: `transform ${durationMs}ms ${EASE_OUT}, opacity ${durationMs}ms ${EASE_OUT}`,
	'@starting-style': {
		opacity: 0,
		transform: `translateX(${from === 'left' ? '-' : ''}${distancePx}px)`,
	},
	'@media (prefers-reduced-motion: reduce)': {
		transition: 'opacity 150ms ease',
		'@starting-style': { opacity: 0, transform: 'none' },
	},
});

export const COMPOSER_ADVISORY_EXIT_MS = 160;
export const getComposerAdvisoryTransitionCss = (placement: 'above' | 'below', visible: boolean) => ({
	transition: visible
		? `transform 220ms ${EASE_OUT}, opacity 160ms ease`
		: `transform ${COMPOSER_ADVISORY_EXIT_MS}ms ${EASE_OUT}, opacity ${COMPOSER_ADVISORY_EXIT_MS}ms ease`,
	willChange: 'transform, opacity',
	opacity: visible ? 1 : 0,
	transform: visible ? 'translateY(0)' : `translateY(${placement === 'above' ? '12px' : '-12px'})`,
	pointerEvents: visible ? 'auto' : 'none',
	'@starting-style': {
		opacity: 0,
		transform: `translateY(${placement === 'above' ? '12px' : '-12px'})`,
	},
	'@media (prefers-reduced-motion: reduce)': {
		transition: 'opacity 140ms ease',
		transform: 'none',
		'@starting-style': { opacity: 0, transform: 'none' },
	},
});
export const TRANSITION_ASSISTANT_SHELL_LEFT = `left 0.2s ${EASE_IN_OUT_STRONG}`;
export const TRANSITION_COMPOSER_DOCK_AXIS = `left 0.2s ${EASE_IN_OUT_STRONG}, right 0.2s ${EASE_IN_OUT_STRONG}`;

/**
 * Fixed-bottom composer/search dock easing up from the bottom edge on entry (@starting-style),
 * while still tracking the sidebar's collapse on desktop. Opening is an entrance, so ease-out;
 * reduced-motion drops the slide and just fades.
 */
export const DURATION_DOCK_SLIDE_MS = 300;
export const DURATION_DOCK_FADE_MS = 250;
export const getDockSlideUpCss = (isMobile: boolean) => ({
	transition: isMobile
		? `transform ${DURATION_DOCK_SLIDE_MS}ms ${EASE_OUT}, opacity ${DURATION_DOCK_FADE_MS}ms ease`
		: `transform ${DURATION_DOCK_SLIDE_MS}ms ${EASE_OUT}, opacity ${DURATION_DOCK_FADE_MS}ms ease`,
	'@starting-style': { transform: 'translateY(100%)', opacity: 0 },
	'@media (prefers-reduced-motion: reduce)': {
		transition: 'opacity 0.2s ease',
		'@starting-style': { transform: 'none', opacity: 0 },
	},
});
export const TRANSITION_SIDEBAR_SIZE = `width 0.2s ${EASE_IN_OUT_STRONG}, min-width 0.2s ${EASE_IN_OUT_STRONG}`;
export const TRANSITION_SIDEBAR_LABEL = `max-width 0.2s ${EASE_IN_OUT_STRONG}, opacity 0.2s ${EASE_IN_OUT_STRONG}`;
export const SIDEBAR_EXPANDED_ACTION_DELAY_MS = 120;
export const DELAY_SIDEBAR_CHILD_REVEAL = '0.12s';
export const DURATION_SIDEBAR_CHILD_REVEAL = '0.16s';
export const DURATION_SIDEBAR_CHILD_REVEAL_OPACITY = '0.12s';

/**
 * Upload queue drawer assembly (edge tab + panel move as one fixed element). The entrance
 * preserves the physical pull-out motion; the shorter exit gets out of the user's way.
 */
export const DURATION_QUEUE_DRAWER_ENTER_MS = 320;
export const DURATION_QUEUE_DRAWER_EXIT_MS = 260;
export const DURATION_QUEUE_DRAWER_BACKDROP_ENTER_MS = 220;
export const DURATION_QUEUE_DRAWER_BACKDROP_EXIT_MS = 180;
export const getQueueDrawerTransition = (open: boolean) =>
	`transform ${open ? DURATION_QUEUE_DRAWER_ENTER_MS : DURATION_QUEUE_DRAWER_EXIT_MS}ms ${EASE_DRAWER}`;
export const getQueueDrawerBackdropTransition = (open: boolean) =>
	`opacity ${open ? DURATION_QUEUE_DRAWER_BACKDROP_ENTER_MS : DURATION_QUEUE_DRAWER_BACKDROP_EXIT_MS}ms ${EASE_OUT}`;

export const DURATION_QUEUE_OVERLAY_ENTER_MS = 180;
export const DURATION_QUEUE_OVERLAY_EXIT_MS = 140;
export const DURATION_QUEUE_OVERLAY_EXPAND_MS = 260;
export const DURATION_QUEUE_OVERLAY_COLLAPSE_MS = 180;
export const getQueueOverlayTransition = (visible: boolean) => {
	const duration = visible ? DURATION_QUEUE_OVERLAY_ENTER_MS : DURATION_QUEUE_OVERLAY_EXIT_MS;
	return `opacity ${duration}ms ${EASE_OUT}, transform ${duration}ms ${EASE_OUT}`;
};
export const getQueueOverlayBodyTransition = (collapsed: boolean) => {
	const duration = collapsed ? DURATION_QUEUE_OVERLAY_COLLAPSE_MS : DURATION_QUEUE_OVERLAY_EXPAND_MS;
	return `grid-template-rows ${duration}ms ${EASE_OUT}, opacity ${duration}ms ${EASE_OUT}, clip-path ${duration}ms ${EASE_OUT}`;
};
export const QUEUE_OVERLAY_REDUCED_MOTION_CSS = {
	transform: 'none',
	transition: 'opacity 120ms ease',
} as const;
export const QUEUE_OVERLAY_BODY_REDUCED_MOTION_CSS = {
	transition: 'opacity 120ms ease',
	clipPath: 'none',
} as const;

/**
 * Upload queue state changes are occasional system feedback, not decorative
 * entrances. Keep the shell stable and animate only the content that changed.
 */
export const QUEUE_STATE_CONTENT_ENTER_CSS = {
	transition: `opacity 180ms ${EASE_OUT}`,
	'@starting-style': {
		opacity: 0,
	},
	'@media (prefers-reduced-motion: reduce)': {
		transition: 'opacity 100ms ease',
		'@starting-style': {
			opacity: 0,
		},
	},
} as const;

export const QUEUE_ACTION_ENTER_CSS = {
	transition: `opacity 140ms ${EASE_OUT}, transform 140ms ${EASE_OUT}`,
	'@starting-style': {
		opacity: 0,
		transform: 'scale(0.96)',
	},
	'&:not(:disabled):active': {
		transform: 'scale(0.97)',
	},
	'@media (prefers-reduced-motion: reduce)': {
		transform: 'none',
		transition: 'opacity 100ms ease',
		'@starting-style': {
			opacity: 0,
			transform: 'none',
		},
		'&:not(:disabled):active': {
			transform: 'none',
		},
	},
} as const;

export const QUEUE_ROW_STATE_TRANSITION_CSS = {
	transition: 'background-color 180ms ease',
	'@media (prefers-reduced-motion: reduce)': {
		transition: 'background-color 100ms ease',
	},
} as const;

/**
 * Short enough to keep up with real transfer progress, which arrives continuously
 * while bytes move. A long ease here would leave the bar trailing the upload it is
 * reporting; it also has to cover the jump to full when a small file lands at once.
 */
export const QUEUE_PROGRESS_RANGE_CSS = {
	transition: 'width 220ms linear, background-color 180ms ease',
	'@media (prefers-reduced-motion: reduce)': {
		transition: 'none',
	},
} as const;

// Breathes while the server stores an already-transferred file. The bar is full and
// static by then, which reads as stalled; this says work continues without claiming
// an outcome, so it stays the same colour — green here would mean done.
//
// Declared through Emotion rather than as a nested `@keyframes` object: the style
// engine treats percentage selectors in an object as property names and appends
// units to their values, emitting `0%,100%:1px` and animating nothing.
const queueProgressSavingPulse = keyframes`
	0%, 100% { opacity: 1; }
	50% { opacity: 0.5; }
`;

export const QUEUE_PROGRESS_SAVING_CSS = {
	animation: `${queueProgressSavingPulse} 1600ms ease-in-out infinite`,
	'@media (prefers-reduced-motion: reduce)': {
		animation: 'none',
	},
} as const;

export const QUEUE_PROGRESS_CIRCLE_RANGE_CSS = {
	transition:
		'stroke 180ms ease, stroke-dashoffset 600ms cubic-bezier(0.2, 0, 0, 1), stroke-dasharray 600ms cubic-bezier(0.2, 0, 0, 1)',
	'@media (prefers-reduced-motion: reduce)': {
		transition: 'none',
	},
} as const;

export const TRANSFER_STEP_TRANSITION_CSS = {
	transition: `opacity 180ms ${EASE_OUT}, transform 180ms ${EASE_OUT}`,
	'@starting-style': {
		opacity: 0,
		transform: 'translateY(8px)',
	},
	'@media (prefers-reduced-motion: reduce)': {
		transform: 'none',
		transition: 'opacity 120ms ease',
		'@starting-style': {
			opacity: 0,
			transform: 'none',
		},
	},
} as const;

export const HEADING_MOUNT_ENTER_CSS = {
	transition: `opacity 200ms ${EASE_OUT}, transform 200ms ${EASE_OUT}`,
	'@starting-style': {
		opacity: 0,
		transform: 'translateY(4px)',
	},
	'@media (prefers-reduced-motion: reduce)': {
		transform: 'none',
		transition: 'opacity 150ms ease',
		'@starting-style': {
			opacity: 0,
			transform: 'none',
		},
	},
} as const;

// New wait-narrative lines fade in as the preparing stage advances.
export const PREPARING_STAGE_TEXT_ENTER_CSS = {
	transition: `opacity 420ms ${EASE_OUT}, filter 420ms ease`,
	'@starting-style': { opacity: 0, filter: 'blur(3px)' },
	'@media (prefers-reduced-motion: reduce)': {
		transition: 'opacity 160ms ease',
		'@starting-style': { opacity: 0, filter: 'none' },
	},
} as const;

//TO DO figure out mobile scale up elastically on press before shrinking
export const DURATION_JUMP_TO_BOTTOM_COLLAPSE_MS = 130;
export const JUMP_TO_BOTTOM_BUTTON_CSS = (isCollapsing: boolean) => ({
	transition: `opacity ${DURATION_JUMP_TO_BOTTOM_COLLAPSE_MS}ms ${EASE_OUT}, transform ${DURATION_JUMP_TO_BOTTOM_COLLAPSE_MS}ms ${EASE_OUT}, background-color 120ms ease, box-shadow 120ms ease`,
	transform: isCollapsing ? 'translateY(18px) scale(0.72)' : 'translateY(0) scale(1)',
	opacity: isCollapsing ? 0 : 1,
	willChange: 'transform, opacity',
	'@starting-style': {
		opacity: 0,
		transform: 'translateY(10px) scale(0.94)',
	},
	'@media (hover: hover) and (pointer: fine)': {
		'&:not(:disabled):hover': {
			transform: isCollapsing ? 'translateY(18px) scale(0.72)' : 'translateY(-1px) scale(1)',
		},
	},
	'&:not(:disabled):active': {
		transform: isCollapsing ? 'translateY(18px) scale(0.72)' : 'translateY(1px) scale(0.96)',
	},
	'@media (prefers-reduced-motion: reduce)': {
		transition: 'opacity 120ms ease',
		transform: 'none',
		'@starting-style': {
			opacity: 0,
			transform: 'none',
		},
		'@media (hover: hover) and (pointer: fine)': {
			'&:not(:disabled):hover': {
				transform: 'none',
			},
		},
		'&:not(:disabled):active': {
			transform: 'none',
		},
	},
});
